// SitePropsPanel.tsx — Phase 4: right-side properties panel for site/elevation mode
// Shows highlighted elevation properties + panel assignment list
// Panels assigned to ANY elevation are unavailable for reassignment (exclusive)

import type { Elevation, PanelData } from '../types/panelvar';
import { CORNER_TYPE_LABELS } from '../types/panelvar';

interface Props {
  elevation: Elevation | null;
  allPanels: PanelData[];
  allElevations: Elevation[];
  onAssign: (elevationId: string, panelId: string) => void;
  onUnassign: (elevationId: string, panelId: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export default function SitePropsPanel({ elevation, allPanels, allElevations, onAssign, onUnassign, collapsed, onToggleCollapse }: Props) {
  if (collapsed) {
    return (
      <div className="site-view-props">
        <button className="props-expand-btn" onClick={onToggleCollapse} title="Expand properties">◀</button>
      </div>
    );
  }

  if (!elevation) {
    return (
      <div className="site-view-props">
        <div className="props-header-row">
          <h3>Properties</h3>
          <button className="props-collapse-btn" onClick={onToggleCollapse} title="Collapse properties">▶</button>
        </div>
        <p className="site-props-hint">Select an elevation to view properties</p>
      </div>
    );
  }

  const assigned = elevation.panelIds
    .map(pid => allPanels.find(p => p.id === pid))
    .filter((p): p is PanelData => p != null);

  // Panels assigned to ANY elevation are not available (exclusive assignment)
  const globallyAssigned = new Set(allElevations.flatMap(e => e.panelIds));
  const unassigned = allPanels.filter(p => !globallyAssigned.has(p.id));

  // Summary stats
  const totalWidth = assigned.reduce((s, p) => s + p.mpvar.width, 0);
  const avgHeight = assigned.length > 0
    ? assigned.reduce((s, p) => s + p.mpvar.height, 0) / assigned.length
    : 0;

  return (
    <div className="site-view-props">
      <div className="props-header-row">
        <h3>{elevation.name}</h3>
        <button className="props-collapse-btn" onClick={onToggleCollapse} title="Collapse properties">▶</button>
      </div>
      <div className="site-props-info">
        <div className="site-props-row">
          <span className="site-props-label">Direction</span>
          <span className="site-props-value">{elevation.direction}</span>
        </div>
        <div className="site-props-row">
          <span className="site-props-label">Panels</span>
          <span className="site-props-value">{assigned.length}</span>
        </div>
        <div className="site-props-row">
          <span className="site-props-label">Total Wall</span>
          <span className="site-props-value">{Math.round(totalWidth / 12)}'-{totalWidth % 12}"</span>
        </div>
        {assigned.length > 0 && (
          <div className="site-props-row">
            <span className="site-props-label">Avg Height</span>
            <span className="site-props-value">{Math.round(avgHeight / 12)}'-{Math.round(avgHeight % 12)}"</span>
          </div>
        )}
        <div className="site-props-row">
          <span className="site-props-label">Left Corner</span>
          <span className="site-props-value">
            {CORNER_TYPE_LABELS[elevation.leftCorner] ?? '—'}
            {elevation.leftCorner !== 'continuous' && elevation.leftConnectsTo && (() => {
              const t = allElevations.find(e => e.id === elevation.leftConnectsTo);
              return t ? ` → ${t.name}` : '';
            })()}
          </span>
        </div>
        <div className="site-props-row">
          <span className="site-props-label">Right Corner</span>
          <span className="site-props-value">
            {CORNER_TYPE_LABELS[elevation.rightCorner] ?? '—'}
            {elevation.rightCorner !== 'continuous' && elevation.rightConnectsTo && (() => {
              const t = allElevations.find(e => e.id === elevation.rightConnectsTo);
              return t ? ` → ${t.name}` : '';
            })()}
          </span>
        </div>
      </div>

      <h4 className="site-props-section-title">Assigned Panels</h4>
      {assigned.length === 0 ? (
        <p className="site-props-hint">No panels assigned yet</p>
      ) : (
        <ul className="site-props-panel-list">
          {assigned.map(p => (
            <li key={p.id} className="site-props-panel-item">
              <span className="site-props-panel-mark">{p.mpvar.panelMark}</span>
              <span className="site-props-panel-dim">
                {Math.round(p.mpvar.width / 12)}' × {Math.round(p.mpvar.height / 12)}'
              </span>
              <button
                className="site-props-remove-btn"
                onClick={() => onUnassign(elevation.id, p.id)}
                title="Remove from elevation"
              >×</button>
            </li>
          ))}
        </ul>
      )}

      <h4 className="site-props-section-title">Available Panels</h4>
      {unassigned.length === 0 ? (
        <p className="site-props-hint">All panels assigned</p>
      ) : (
        <ul className="site-props-panel-list">
          {unassigned.map(p => (
            <li key={p.id} className="site-props-panel-item unassigned">
              <span className="site-props-panel-mark">{p.mpvar.panelMark}</span>
              <span className="site-props-panel-dim">
                {Math.round(p.mpvar.width / 12)}' × {Math.round(p.mpvar.height / 12)}'
              </span>
              <button
                className="site-props-assign-btn"
                onClick={() => onAssign(elevation.id, p.id)}
                title="Assign to this elevation"
              >+</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
